import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Link as MuiLink,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../contexts/AuthContext';
import { listGuests, deleteGuest, eraseGuest } from '../../services/guestsApi';
import type { EventGuest } from '../../types/eventGuest';
import GuestCard from '../../components/guests/GuestCard';
import GuestForm from '../../components/guests/GuestForm';
import EraseGuestDialog from '../../components/guests/EraseGuestDialog';

/**
 * Plan 4.9 — MyGuestsPage
 *
 * Lists the parent's saved event guests with full CRUD + GDPR erase.
 * Empty state explains what guests are + the data-use posture, with a
 * Privacy Policy link.
 *
 * Refresh affordances mirror Plan 4F:
 *   - visibilitychange → auto-refetch on foreground
 *   - explicit Refresh icon in the page header
 */
const MyGuestsPage: React.FC = () => {
  const { user } = useAuth();
  const familyId =
    user?.familyMembers?.find((m) => m.crmFamilyId)?.crmFamilyId ?? null;

  const [guests, setGuests] = useState<EventGuest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingGuest, setEditingGuest] = useState<EventGuest | null>(null);
  const [erasingGuest, setErasingGuest] = useState<EventGuest | null>(null);

  const loadGuests = useCallback(async () => {
    if (!familyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listGuests(familyId);
      setGuests(data.filter((g) => !g.isRedacted));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load guests');
    } finally {
      setLoading(false);
    }
  }, [familyId]);

  useEffect(() => {
    void loadGuests();
  }, [loadGuests]);

  useEffect(() => {
    const handler = () => {
      if (!document.hidden) void loadGuests();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [loadGuests]);

  const handleDelete = async (guest: EventGuest) => {
    if (!familyId) return;
    try {
      await deleteGuest(familyId, guest.id);
      setGuests((prev) => prev.filter((g) => g.id !== guest.id));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete guest');
    }
  };

  const handleErase = async () => {
    if (!erasingGuest || !familyId) return;
    try {
      await eraseGuest(familyId, erasingGuest.id);
      setGuests((prev) => prev.filter((g) => g.id !== erasingGuest.id));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to erase guest');
    } finally {
      setErasingGuest(null);
    }
  };

  if (!familyId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Your account isn't linked to a family record yet. Once it is, you'll
          be able to manage event guests here.
        </Alert>
      </Container>
    );
  }

  const isEmpty = !loading && guests.length === 0;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          My Guests
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadGuests} aria-label="Refresh guests">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {isEmpty && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 2 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Guests are people you can register for events alongside your family,
            like grandparents, aunts and uncles, or family friends.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We only store these details for event registration and emergency
            contact. See our{' '}
            <MuiLink href="/support" target="_blank" rel="noopener">
              Privacy Policy
            </MuiLink>
            .
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowForm(true)}
          >
            Add your first guest
          </Button>
        </Paper>
      )}

      {!isEmpty && !loading && (
        <Stack spacing={0}>
          {guests.map((guest) => (
            <GuestCard
              key={guest.id}
              guest={guest}
              onEdit={() => setEditingGuest(guest)}
              onDelete={() => handleDelete(guest)}
              onErase={() => setErasingGuest(guest)}
            />
          ))}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowForm(true)}
            >
              Add another guest
            </Button>
          </Box>
        </Stack>
      )}

      {showForm && (
        <Dialog open onClose={() => setShowForm(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Add a guest</DialogTitle>
          <DialogContent>
            <GuestForm
              mode="create"
              familyId={familyId}
              onCancel={() => setShowForm(false)}
              onSuccess={(guest) => {
                setShowForm(false);
                setGuests((prev) => [...prev, guest]);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {editingGuest && (
        <Dialog
          open
          onClose={() => setEditingGuest(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Edit guest</DialogTitle>
          <DialogContent>
            <GuestForm
              mode="edit"
              familyId={familyId}
              initialValue={editingGuest}
              onCancel={() => setEditingGuest(null)}
              onSuccess={(updated) => {
                setEditingGuest(null);
                setGuests((prev) =>
                  prev.map((g) => (g.id === updated.id ? updated : g)),
                );
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {erasingGuest && (
        <EraseGuestDialog
          open
          guest={erasingGuest}
          onCancel={() => setErasingGuest(null)}
          onConfirm={handleErase}
        />
      )}
    </Container>
  );
};

export default MyGuestsPage;
